import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { makeRequestAuth } from "../../_config/api";
import { ToastContainer } from "react-toastify";
import {
  FiArrowLeft,
  FiPlus,
  FiBook,
  FiTrash2,
  FiEdit2,
  FiLayers,
} from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import NoData from "../../utils/DataNotFound";
import AddSubjectModal from "./AddSubjectModal";
import EditSubjectModal from "./EditSubjectModal";

export default function SubjectList() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const practiceName = location.state?.practiceName || "Practice";
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchSubjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth(
        "post",
        "/admin/getSubjectsByPractice",
        { practiceId: id },
      );
      setSubjects(res?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchSubjects();
  }, [id, fetchSubjects]);

  const handleDelete = async (subjectId) => {
    if (!window.confirm("Are you sure you want to delete this subject?"))
      return;
    try {
      await makeRequestAuth("post", "/admin/deleteSubject", { subjectId });
      showSuccessToast("Subject deleted successfully");
      fetchSubjects();
    } catch (error) {
      showErrorToast(
        error?.response?.data?.message || "Failed to delete subject",
      );
    }
  };

  // --- NAVIGATE TO PAPERS PAGE ---
  const handleViewPapers = (subject) => {
    navigate(`/admin/subject-papers/${subject._id}`, {
      state: { subjectName: subject.name, practiceName: practiceName },
    });
  };

  return (
    <div className="p-4">
      <ToastContainer />

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-600 transition"
          >
            <FiArrowLeft size={22} />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 capitalize">
              {practiceName}
            </h2>
            <p className="text-gray-500 text-sm">Subject Management</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-lg shadow-md transition-all active:scale-95"
        >
          <FiPlus size={18} /> <span>Add Subject</span>
        </button>
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading subjects...</div>
      ) : subjects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
          <NoData title="No Subjects Found" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Desktop Table */}
          <table className="w-full text-left border-collapse hidden md:table">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider font-semibold border-b">
              <tr>
                <th className="px-6 py-4 w-16 text-center">#</th>
                <th className="px-6 py-4">Subject Name</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subjects.map((sub, index) => (
                <tr
                  key={sub._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 text-center text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-50 text-purple-600 p-2 rounded-lg">
                        <FiBook />
                      </div>
                      <span className="font-medium text-gray-800">
                        {sub.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {/* Papers Button */}
                      <button
                        onClick={() => handleViewPapers(sub)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors mr-2"
                      >
                        <FiLayers size={14} /> Papers
                      </button>
                      <button
                        onClick={() => setEditData(sub)}
                        className="p-2 text-blue-600 bg-white border hover:bg-blue-50 rounded-lg"
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(sub._id)}
                        className="p-2 text-red-600 bg-white border hover:bg-red-50 rounded-lg"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile View */}
          <div className="md:hidden divide-y divide-gray-100">
            {subjects.map((sub, index) => (
              <div key={sub._id} className="p-4 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm font-mono">
                    {index + 1}.
                  </span>
                  <h4 className="font-semibold text-gray-800">{sub.name}</h4>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <button
                    onClick={() => handleViewPapers(sub)}
                    className="text-sm font-medium text-teal-600 bg-teal-50 px-3 py-1.5 rounded-lg flex items-center gap-2"
                  >
                    <FiLayers /> View Papers
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditData(sub)}
                      className="p-2 text-blue-600 bg-gray-50 rounded-lg border"
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(sub._id)}
                      className="p-2 text-red-600 bg-gray-50 rounded-lg border"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddSubjectModal
          practiceId={id}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchSubjects();
          }}
        />
      )}
      {editData && (
        <EditSubjectModal
          data={editData}
          onClose={() => setEditData(null)}
          onSuccess={() => {
            setEditData(null);
            fetchSubjects();
          }}
        />
      )}
    </div>
  );
}
